import React, { useEffect, useState } from 'react'
import { Alert, Button, CircularProgress, Tooltip, Typography } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import SearchIcon from '@mui/icons-material/Search'
import RefreshIcon from '@mui/icons-material/Refresh'
import AcUnitIcon from '@mui/icons-material/AcUnit'
import AirIcon from '@mui/icons-material/Air'
import SyncPriceButton from '../../components/global/SyncPriceButton'
import AppDialog from '../../components/dialog/AppDialog'
import DirectSapPriceDialog from './components/DirectSapPriceDialog'
import { ApiService } from '@carrier-ecat/workflowui-globalfunctions'
import { apiUrl } from '../../environments/environments'
import './index.css'

const BUILDERS = [
    { key: 'fancoils', label: 'Fancoils', icon: <AcUnitIcon /> },
    { key: 'drycooler', label: 'Dry Cooler', icon: <AirIcon /> },
]

function SapDirectPricesPage({ match, history: routeHistory }) {
    const routeBuilder = match?.params?.builder || window.location.pathname.split('/').pop()
    const history = routeHistory || { push: (path) => window.history.pushState({}, '', path) }
    const builder = BUILDERS.some((item) => item.key === routeBuilder) ? routeBuilder : 'fancoils'
    const builderLabel = BUILDERS.find((item) => item.key === builder).label
    const directPricesUrl = `${apiUrl}/SAPDirect/${builder}/prices`
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [selectedRow, setSelectedRow] = useState(null)
    const [directOpen, setDirectOpen] = useState(false)
    const [refreshingRowId, setRefreshingRowId] = useState(null)
    const [rowErrors, setRowErrors] = useState({})

    useEffect(() => {
        setError('')
        loadRows()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [builder])

    const loadRows = async () => {
        setLoading(true)
        setError('')
        try {
            const { data } = await ApiService(directPricesUrl, 'get', null, null, 0, { limit: 100 })
            setRows(Array.isArray(data?.entries) ? data.entries : [])
        } catch (err) {
            setRows([])
            setError(err.response?.data?.error || 'Unable to load SAP Direct prices.')
        } finally {
            setLoading(false)
        }
    }

    const clearRowError = (rowId) => {
        setRowErrors((prev) => {
            if (!(rowId in prev)) return prev
            const next = { ...prev }
            delete next[rowId]
            return next
        })
    }

    const refreshRow = async (row) => {
        setRefreshingRowId(row.id)
        clearRowError(row.id)
        try {
            await ApiService(`${directPricesUrl}/${row.id}/refresh`, 'post', {}, null, 0)
            await loadRows()
        } catch (err) {
            const message = err.response?.data?.error || `Unable to refresh ${row.kmat} (${row.catalog}) from SAP.`
            setRowErrors((prev) => ({ ...prev, [row.id]: message }))
        } finally {
            setRefreshingRowId(null)
        }
    }

    const openRow = async (row) => {
        try {
            const { data } = await ApiService(`${directPricesUrl}/${row.id}`, 'get', null, null, 0)
            setSelectedRow(data)
            clearRowError(row.id)
        } catch (err) {
            setRowErrors((prev) => ({ ...prev, [row.id]: err.response?.data?.error || 'Unable to load price details.' }))
        }
    }

    const columns = [
        {
            field: 'kmat',
            headerName: 'KMAT',
            flex: 1.1,
            minWidth: 150,
            renderCell: ({ row, value }) =>
                row.__isErrorRow ? <span className='sap-direct-price-error-cell'>{row.__errorMessage}</span> : value,
        },
        { field: 'catalog', headerName: 'Catalog', flex: 1, minWidth: 140 },
        { field: 'catalog2', headerName: 'Catalog 2', flex: 1, minWidth: 120 },
        { field: 'catalog3', headerName: 'Catalog 3', flex: 1, minWidth: 120 },
        { field: 'totalPrice', headerName: 'Total price', flex: 0.8, minWidth: 130 },
        {
            field: 'updatedAt',
            headerName: 'Last update',
            flex: 1.2,
            minWidth: 180,
            valueFormatter: ({ value }) => (value ? new Date(value).toLocaleString() : ''),
        },
        {
            field: 'actions',
            headerName: '',
            sortable: false,
            filterable: false,
            width: 62,
            renderCell: ({ row }) => {
                const isRefreshing = refreshingRowId === row.id
                return (
                    <Tooltip title={isRefreshing ? 'Refreshing...' : 'Refresh price'}>
                        <span>
                            <Button
                                aria-label={`Refresh ${row.kmat}`}
                                size='small'
                                disabled={isRefreshing}
                                onClick={(event) => {
                                    event.stopPropagation()
                                    refreshRow(row)
                                }}>
                                {isRefreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize='small' />}
                            </Button>
                        </span>
                    </Tooltip>
                )
            },
        },
    ]

    // Spans the full row width so a failed action gets its own dedicated line, right after the row it refers to.
    columns[0].colSpan = (params) => (params.row.__isErrorRow ? columns.length : undefined)

    const displayRows = rows.flatMap((row) => {
        const message = rowErrors[row.id]
        if (!message) return [row]
        return [row, { id: `error-${row.id}`, __isErrorRow: true, __errorMessage: message }]
    })


    return (
        <div className='page-container sap-direct-prices-page'>
            <div className='sap-direct-price-page-header'>
                <div>
                    <Typography component='h1' variant='h4'>SAP Direct Pricing</Typography>
                    <Typography variant='body1'>Inspect and refresh SAP pricing requests for each builder.</Typography>
                </div>
            </div>
            <div className='sap-direct-price-builder-tabs'>
                {BUILDERS.map((item) => (
                    <button
                        type='button'
                        key={item.key}
                        className={item.key === builder ? 'active' : ''}
                        onClick={() => history.push(`/sap-direct/${item.key}`)}>
                        {item.icon}
                        {item.label}
                    </button>
                ))}
            </div>
            <section className='sap-direct-price-table-section'>
                <div className='sap-direct-price-section-heading'>
                    <div>
                        <Typography component='h2' variant='h6'>Pricing Data</Typography>
                        <Typography variant='body2'>{`All ${builderLabel} SAP Direct prices. Use the column filters to search.`}</Typography>
                    </div>
                    <div className='sap-direct-price-table-actions'>
                        <Button variant='outlined' startIcon={<SearchIcon />} onClick={() => setDirectOpen(true)}>
                            Query SAP
                        </Button>
                        <SyncPriceButton
                            key={builder}
                            builder={builder}
                            onSyncComplete={loadRows}
                            onError={(message) => setError(message)}
                            showInlineError={false}
                            initialMessage='Refresh Prices'
                            syncUrl={`${directPricesUrl}/refresh`}
                            syncMethod='post'
                            syncParams={{}}
                        />
                    </div>
                </div>
                {error && <Alert severity='error'>{error}</Alert>}
                <div className='sap-direct-price-grid'>
                    <DataGrid
                        rows={displayRows}
                        columns={columns}
                        loading={loading}
                        pageSize={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        disableSelectionOnClick
                        onRowClick={({ row }) => {
                            if (row.__isErrorRow) return
                            openRow(row)
                        }}
                        autoHeight
                        getRowClassName={({ row }) => (row.__isErrorRow ? 'sap-direct-price-error-row' : '')}
                        localeText={{ noRowsLabel: 'No SAP Direct prices yet. Use Refresh Prices to populate this builder.' }}
                    />
                </div>
            </section>
            <AppDialog
                open={Boolean(selectedRow)}
                onClose={() => setSelectedRow(null)}
                title='Pricing details'
                actions={<Button onClick={() => setSelectedRow(null)}>Close</Button>}>
                <pre className='sap-direct-price-json'>{JSON.stringify(selectedRow?.response || {}, null, 2)}</pre>
            </AppDialog>
            <DirectSapPriceDialog
                open={directOpen}
                builder={builder}
                onClose={() => setDirectOpen(false)}
                onPersisted={loadRows}
            />
        </div>
    )
}

export default SapDirectPricesPage